import EnrollmentForm from "@/components/enrollment/EnrollmentForm";
import RefundForm from "@/components/enrollment/RefundForm";
import useAuth from "@/lib/auth/useAuth";
import useRequest from "@/lib/request/useRequest";
import { formatCurrency } from "@/lib/utils/currency";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import {
    EnrollmentTier,
    getEnrollmentTierLabel,
    RequestHelpType,
} from "@/lib/utils/enrollment";
import { Status } from "@/types/enums";
import { FieldWithEnrollment, ImageryExtent } from "@/types/field";
import {
    Organization,
    OrganizationAvailableCredits,
} from "@/types/organization";
import { PaginationResponse } from "@/types/pagination";
import {
    faDownload,
    faArrowUpShortWide,
    faArrowDownShortWide,
    faPencil,
    faArrowRotateLeft,
    faPlus,
    faMoneyCheck,
    faBars,
    faToggleOn,
    faToggleOff,
    faFileZipper,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    addToast,
    Button,
    ButtonGroup,
    Card,
    CardBody,
    CardHeader,
    Checkbox,
    Chip,
    Divider,
    Spinner,
    Switch,
    Table,
    TableColumn,
    TableHeader,
    TableBody,
    TableRow,
    TableCell,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    useDisclosure,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    Select,
    SelectItem,
} from "@heroui/react";
import { createFileRoute } from "@tanstack/react-router";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    Row,
    RowSelectionState,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import sentinelLogo from "@/assets/images/Sentinel_Horizontal_TM.png";
import { Link } from "@tanstack/react-router";
import { FieldFilterBar } from "@/components/fields/FieldFilterBar";
import { useI18n } from "@/lib/i18n/useI18n";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import useBulkBoundaryDownload from "@/lib/utils/useBulkBoundaryDownload";
import { useInfiniteQuery } from "@tanstack/react-query";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

const SENTRY_PROVIDER_ID = 77; // Demo Fields

type FieldStatusFilter = "active" | "inactive" | "all";
const FIELD_STATUS_VALUES: Record<FieldStatusFilter, Status | undefined> = {
    active: Status.Active,
    inactive: Status.Inactive,
    all: undefined,
};

type EnrollmentTotalsResponse = {
    enrollments: {
        enrollment_tier: EnrollmentTier;
        acres: number;
        value: number;
    }[];
    active_unenrolled_acres: number;
    inactive_unenrolled_acres: number;
};

const searchSchema = z.object({
    search: z.string().optional(),
    service_provider: z.string().optional(),
    grower: z.string().optional(),
    status: z.enum(["active", "inactive", "all"]).optional().catch(undefined),
});

export const Route = createFileRoute("/dashboard/enrollment/fields")({
    component: RouteComponent,
    validateSearch: zodValidator(searchSchema),
});

function RouteComponent() {
    const { useGetRequest, useMutateRequest, axiosRequest } = useRequest();
    const { formatDateMMDDYYYY } = useI18n();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { userIsSentinel, isConsultant, isConsultantOrSentinel } = useAuth();
    const {
        organizationPreviewEnabled,
        organizationMonitoringEnabled,
        enrollmentFieldsPaginationEnabled,
        isFetched: featureFlagsFetched,
    } = useFeatureFlag();

    const usePaginatedEnrollment =
        userIsSentinel &&
        featureFlagsFetched &&
        enrollmentFieldsPaginationEnabled;
    const waitingForSentinelFeatureFlags =
        userIsSentinel && !featureFlagsFetched;
    const navigate = Route.useNavigate();
    const {
        search,
        service_provider: serviceProviderFilter,
        grower: farmFilter,
        status,
    } = Route.useSearch();
    const searchValue = search ?? "";
    const fieldStatusFilter = status ?? "active";
    const fieldStatus = FIELD_STATUS_VALUES[fieldStatusFilter];
    const setSearchValue = useCallback(
        (value: string) => {
            navigate({
                search: (prev) => ({
                    ...prev,
                    search: value || undefined,
                }),
            });
        },
        [navigate],
    );
    const setServiceProviderFilter = useCallback(
        (value: string | undefined) => {
            navigate({
                search: (prev) => ({
                    ...prev,
                    service_provider: value,
                }),
            });
        },
        [navigate],
    );
    const setFarmFilter = useCallback(
        (value: string | undefined) => {
            navigate({
                search: (prev) => ({
                    ...prev,
                    grower: value,
                }),
            });
        },
        [navigate],
    );
    const setFieldStatusFilter = useCallback(
        (value: FieldStatusFilter) => {
            navigate({
                search: (prev) => ({
                    ...prev,
                    status: value,
                }),
            });
        },
        [navigate],
    );
    const [sorting, setSorting] = useState<SortingState>([
        {
            id: "enrollment",
            desc: false,
        },
    ]);
    const primarySort = sorting[0];
    const effectiveOrdering = primarySort
        ? `${primarySort.desc ? "-" : ""}${primarySort.id}`
        : "enrollment";
    const [isLoadingAllFilteredRows, setIsLoadingAllFilteredRows] =
        useState(false);
    const allRowsRequestRef = useRef(0);
    const totalsRequestRef = useRef(0);

    const invalidateAllRowsRequest = useCallback(() => {
        allRowsRequestRef.current += 1;
        totalsRequestRef.current += 1;
        setIsLoadingAllFilteredRows(false);
        onClose();
    }, [onClose]);

    const enrollmentQuery = useGetRequest<FieldWithEnrollment[]>({
        url: "fields/enrollment_status/",
        enabled: !waitingForSentinelFeatureFlags && !usePaginatedEnrollment,
        queryOptions: {
            refetchOnWindowFocus: false,
        },
    });
    const paginatedEnrollmentQuery = useInfiniteQuery<
        PaginationResponse<FieldWithEnrollment>
    >({
        queryKey: [
            "fields",
            "enrollment_status_paginated",
            searchValue,
            serviceProviderFilter,
            farmFilter,
            fieldStatus,
            effectiveOrdering,
        ],
        enabled: usePaginatedEnrollment,
        getNextPageParam: (lastPage) => {
            return lastPage.next ? lastPage.page + 1 : undefined;
        },
        getPreviousPageParam: (firstPage) => {
            return firstPage.previous ? firstPage.page - 1 : undefined;
        },
        initialPageParam: 1,
        queryFn: async ({ pageParam = 1 }) => {
            return await axiosRequest<PaginationResponse<FieldWithEnrollment>>({
                url: "fields/enrollment_status_paginated/",
                params: {
                    search: searchValue || undefined,
                    service_provider: serviceProviderFilter,
                    grower: farmFilter,
                    status: fieldStatus,
                    ordering: effectiveOrdering,
                    page: pageParam,
                    size: 40,
                },
            });
        },
    });
    const {
        data: enrollmentTotals,
        isFetching: isFetchingEnrollmentTotals,
        refetch: refetchEnrollmentTotals,
    } = useGetRequest<EnrollmentTotalsResponse>({
        url: "fields/enrollment_status_totals/",
        enabled: false,
        params: {
            ...(searchValue ? { search: searchValue } : {}),
            ...(serviceProviderFilter
                ? { service_provider: serviceProviderFilter }
                : {}),
            ...(farmFilter ? { grower: farmFilter } : {}),
        },
        queryOptions: {
            refetchOnWindowFocus: false,
        },
    });
    const remainingBalance = useGetRequest<{
        remaining_retail_value: number;
    }>({
        url: "field_enrollments/remaining_retail_value/",
    });
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [showEnrollmentForm, setShowEnrollmentForm] =
        useState<boolean>(false);
    const [showRefundForm, setShowRefundForm] = useState<boolean>(false);

    const patchField = useMutateRequest({
        method: "patch",
        url: "fields/bulk_update/",
        errorMessage: "Failed to update field",
        successMessage: "Successfully updated field",
    });

    const refetchEnrollmentFields = useCallback(() => {
        if (usePaginatedEnrollment) {
            invalidateAllRowsRequest();
            paginatedEnrollmentQuery.refetch();
            return;
        }
        enrollmentQuery.refetch();
    }, [
        enrollmentQuery,
        invalidateAllRowsRequest,
        paginatedEnrollmentQuery,
        usePaginatedEnrollment,
    ]);

    const columns: ColumnDef<FieldWithEnrollment>[] = useMemo(() => {
        const tableColumns: ColumnDef<FieldWithEnrollment>[] = [
            {
                id: "select",
                header: ({ table }) => {
                    return (
                        <Checkbox
                            className="pl-4"
                            isSelected={table.getIsAllRowsSelected()}
                            onValueChange={(value) => {
                                table.toggleAllRowsSelected(value);
                            }}
                        />
                    );
                },
                enableSorting: false,
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.id;
                },
                size: 50,
                cell: ({ row }) => {
                    return (
                        <Checkbox
                            className="pl-4"
                            isSelected={row.getIsSelected()}
                            onValueChange={(value) => row.toggleSelected(value)}
                        />
                    );
                },
            },
            {
                header: "Active",
                id: "status",
                accessorKey: "status",
                cell: ({ row }) => {
                    return (
                        <Switch
                            size="sm"
                            isSelected={row.original.status == 1}
                            onValueChange={(isSelected) => {
                                patchField.mutate(
                                    {
                                        data: [
                                            {
                                                id: row.original.id,
                                                status: isSelected ? 1 : 2,
                                            },
                                        ],
                                    },
                                    {
                                        onSuccess: () => {
                                            refetchEnrollmentFields();
                                        },
                                    },
                                );
                            }}
                        />
                    );
                },
                size: 80,
            },
            {
                id: "farm",
                header: "Farm/CSP",
                accessorFn: (row) => row.grower.name,
                cell: ({ row }) => {
                    return (
                        <div className="flex flex-col">
                            <div>{row.original.grower?.name}</div>
                            <div className="text-xs text-gray-500">
                                {row.original.grower?.service_provider?.name ??
                                    "N/A"}
                            </div>
                        </div>
                    );
                },
                size: 250,
            },
            {
                id: "field",
                header: "Field",
                accessorFn: (row) => row.name,
                cell: ({ row }) => {
                    return (
                        <Link
                            to="/dashboard/fields/$fieldId/setup"
                            params={{ fieldId: row.original.id.toString() }}
                            target="_blank"
                        >
                            <div className="flex flex-col items-start justify-between w-full">
                                {row.original.name}
                                <div className="flex flex-row gap-1 justify-start">
                                    <Chip
                                        color={
                                            row.original.active_enrollment
                                                ? "primary"
                                                : "default"
                                        }
                                        radius="sm"
                                        classNames={{
                                            base: "text-[0.6rem]",
                                            content: "p-[0.15rem]",
                                        }}
                                    >
                                        {getEnrollmentTierLabel(
                                            row.original.active_enrollment
                                                ?.enrollment_tier,
                                        )}
                                    </Chip>
                                    {!!row.original.active_enrollment && (
                                        <Chip
                                            color={
                                                row.original.active_enrollment
                                                    .insights_active
                                                    ? "secondary"
                                                    : "default"
                                            }
                                            radius="sm"
                                            classNames={{
                                                base: "text-[0.6rem]",
                                                content: "p-[0.15rem]",
                                            }}
                                        >
                                            {row.original.active_enrollment
                                                .insights_active
                                                ? "Insights Active"
                                                : "Insights Inactive"}
                                        </Chip>
                                    )}
                                </div>
                            </div>
                        </Link>
                    );
                },
                size: 250,
            },
            {
                id: "enrollment",
                header: "Enrollment",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.active_enrollment?.start_date;
                },
                cell: ({ row }) => {
                    const e = row.original.active_enrollment;
                    if (!e) return null;
                    const today = getTodayWithTimeTravel();
                    const end = dayjs(e.end_date);
                    const daysUntilEnd = end.diff(today, "day");
                    const enrollmentExpiresSoon =
                        daysUntilEnd >= 0 && daysUntilEnd <= 30;
                    return (
                        <div
                            className={classNames(
                                "flex flex-col rounded-md px-1.5 py-1 mx-1 -my-0.5",
                                enrollmentExpiresSoon && "bg-warning-200",
                            )}
                            title={
                                enrollmentExpiresSoon
                                    ? "Enrollment ends within 30 days"
                                    : undefined
                            }
                        >
                            <div>{formatDateMMDDYYYY(e.start_date)}</div>
                            <div className="text-xs text-gray-500">
                                {formatDateMMDDYYYY(e.end_date)}
                            </div>
                        </div>
                    );
                },
                size: 110,
            },
            {
                id: "insights",
                header: "Insights",
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.active_enrollment?.insights_start_date;
                },
                cell: ({ row }) => {
                    const e = row.original.active_enrollment;
                    if (!e) return null;
                    return (
                        <div className="flex flex-col">
                            <div>
                                {formatDateMMDDYYYY(e.insights_start_date)}
                            </div>
                            <div className="text-xs text-gray-500">
                                {formatDateMMDDYYYY(e.insights_end_date)}
                            </div>
                        </div>
                    );
                },
                size: 120,
            },
            {
                id: "acres",
                header: "Acres",
                enableSorting: !usePaginatedEnrollment,
                accessorFn: (row: FieldWithEnrollment) => {
                    return (
                        row.active_enrollment?.acres ??
                        row.imagery_boundary_acres
                    );
                },
                size: 100,
            },
            {
                id: "boundaryType",
                header: "Boundary Type",
                enableSorting: !usePaginatedEnrollment,
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.imagery_extent;
                },
                cell: ({ row }) => {
                    return (
                        <ButtonGroup
                            size="sm"
                            isDisabled={!!row.original.active_enrollment}
                        >
                            <Button
                                size="sm"
                                color={
                                    row.original.imagery_extent ==
                                        ImageryExtent.FIELD
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() => {
                                    patchField.mutate(
                                        {
                                            data: [
                                                {
                                                    id: row.original.id,
                                                    imagery_extent:
                                                        ImageryExtent.FIELD,
                                                },
                                            ],
                                        },
                                        {
                                            onSuccess: () => {
                                                refetchEnrollmentFields();
                                            },
                                        },
                                    );
                                }}
                            >
                                Field
                            </Button>
                            <Button
                                size="sm"
                                color={
                                    row.original.imagery_extent ==
                                        ImageryExtent.FIELD
                                        ? "default"
                                        : "primary"
                                }
                                isDisabled={!row.original.has_irrigation_system}
                                onPress={() => {
                                    patchField.mutate(
                                        {
                                            data: [
                                                {
                                                    id: row.original.id,
                                                    imagery_extent:
                                                        ImageryExtent.IRRIGATED_REGION,
                                                },
                                            ],
                                        },
                                        {
                                            onSuccess: () => {
                                                refetchEnrollmentFields();
                                            },
                                        },
                                    );
                                }}
                            >
                                Irrigated
                            </Button>
                        </ButtonGroup>
                    );
                },
            },
            {
                id: "price",
                header: "Price",
                enableSorting: !usePaginatedEnrollment,
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.active_enrollment?.retail_price_per_acre;
                },
                cell: ({ row }) => {
                    if (!row.original.active_enrollment) {
                        return <div></div>;
                    }
                    return (
                        <div>
                            {formatCurrency(
                                row.original.active_enrollment
                                    ?.retail_price_per_acre,
                            )}
                        </div>
                    );
                },
                size: 100,
            },
            {
                id: "total",
                header: "Total",
                enableSorting: !usePaginatedEnrollment,
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.active_enrollment?.credits;
                },
                cell: ({ row }) => {
                    if (!row.original.active_enrollment) {
                        return <div></div>;
                    }
                    return (
                        <div>
                            {formatCurrency(
                                row.original.active_enrollment?.credits,
                            )}
                        </div>
                    );
                },
                size: 100,
            },
            {
                id: "actions",
                header: "Actions",
                enableSorting: !usePaginatedEnrollment,
                accessorFn: (row: FieldWithEnrollment) => {
                    return row.id;
                },
                cell: ({ row }) => {
                    return (
                        <div className="flex flex-row gap-1 w-full">
                            {!row.original.active_enrollment && (
                                <Button
                                    color="secondary"
                                    size="sm"
                                    fullWidth
                                    onPress={() => {
                                        setRowSelection({
                                            [row.original.id]: true,
                                        });
                                        setShowEnrollmentForm(true);
                                    }}
                                    startContent={
                                        <FontAwesomeIcon icon={faPlus} />
                                    }
                                >
                                    Enroll
                                </Button>
                            )}
                            {row.original.active_enrollment && (
                                <Button
                                    color="primary"
                                    size="sm"
                                    fullWidth
                                    onPress={() => {
                                        setRowSelection({
                                            [row.original.id]: true,
                                        });
                                        setShowEnrollmentForm(true);
                                    }}
                                    startContent={
                                        <FontAwesomeIcon icon={faPencil} />
                                    }
                                >
                                    Edit
                                </Button>
                            )}
                            {row.original.active_enrollment &&
                                !row.original.active_enrollment.refunded_on &&
                                userIsSentinel && (
                                    <Button
                                        color="primary"
                                        size="sm"
                                        fullWidth
                                        startContent={
                                            <FontAwesomeIcon
                                                icon={faArrowRotateLeft}
                                            />
                                        }
                                        onPress={() => {
                                            setRowSelection({
                                                [row.original.id]: true,
                                            });
                                            setShowRefundForm(true);
                                        }}
                                    >
                                        Refund
                                    </Button>
                                )}
                        </div>
                    );
                },
            },
        ];

        return tableColumns;
    }, [
        patchField,
        refetchEnrollmentFields,
        usePaginatedEnrollment,
        userIsSentinel,
        formatDateMMDDYYYY,
    ]);

    const serviceProvidersQuery = useGetRequest<Organization[]>({
        url: "organizations/service_providers/",
        enabled: usePaginatedEnrollment && userIsSentinel,
    });

    const growersQuery = useGetRequest<Organization[]>({
        url: "organizations/farms/",
        enabled: usePaginatedEnrollment && isConsultantOrSentinel,
    });

    const paginatedRows = useMemo(() => {
        return (
            paginatedEnrollmentQuery.data?.pages.flatMap(
                (page) => page.results,
            ) ?? []
        );
    }, [paginatedEnrollmentQuery.data?.pages]);

    const legacyServiceProviderOptions = useMemo(() => {
        return (enrollmentQuery?.data ?? [])
            ?.map((field) => field.grower.service_provider)
            .filter(
                (sp, index, self) =>
                    sp && index === self.findIndex((s) => s?.id === sp.id),
            ) as Organization[];
    }, [enrollmentQuery?.data]);

    const legacyFarmOptions = useMemo(() => {
        return (enrollmentQuery?.data ?? [])
            ?.map((field) => field.grower)
            .filter(
                (grower, index, self) =>
                    index === self.findIndex((g) => g.id === grower.id),
            ) as Organization[];
    }, [enrollmentQuery?.data]);

    const serviceProviderOptions = useMemo(() => {
        return usePaginatedEnrollment
            ? (serviceProvidersQuery.data ?? [])
            : legacyServiceProviderOptions;
    }, [
        legacyServiceProviderOptions,
        serviceProvidersQuery.data,
        usePaginatedEnrollment,
    ]);

    const farmOptions = useMemo(() => {
        return usePaginatedEnrollment
            ? (growersQuery.data ?? [])
            : legacyFarmOptions;
    }, [growersQuery.data, legacyFarmOptions, usePaginatedEnrollment]);

    const legacyTotalsRows = useMemo(() => {
        let rows = enrollmentQuery?.data ?? [];
        if (searchValue) {
            rows = rows.filter(
                (row) =>
                    row.name
                        .toLowerCase()
                        .includes(searchValue.toLowerCase()) ||
                    row.grower.name
                        .toLowerCase()
                        .includes(searchValue.toLowerCase()) ||
                    row.grower.service_provider?.name
                        ?.toLowerCase()
                        .includes(searchValue.toLowerCase()),
            );
        }
        if (serviceProviderFilter) {
            rows = rows.filter(
                (row) =>
                    row.grower.service_provider?.id.toString() ===
                    serviceProviderFilter,
            );
        }
        if (farmFilter) {
            rows = rows.filter(
                (row) => row.grower.id.toString() === farmFilter,
            );
        }
        return rows;
    }, [
        enrollmentQuery?.data,
        searchValue,
        serviceProviderFilter,
        farmFilter,
    ]);

    const legacyFilteredRows = useMemo(() => {
        if (fieldStatus === undefined) return legacyTotalsRows;
        return legacyTotalsRows.filter((row) => row.status === fieldStatus);
    }, [fieldStatus, legacyTotalsRows]);

    const tableRows = useMemo(() => {
        return usePaginatedEnrollment ? paginatedRows : legacyFilteredRows;
    }, [legacyFilteredRows, paginatedRows, usePaginatedEnrollment]);

    useEffect(() => {
        if (!usePaginatedEnrollment) return;
        setRowSelection({});
    }, [
        effectiveOrdering,
        farmFilter,
        searchValue,
        serviceProviderFilter,
        fieldStatus,
        usePaginatedEnrollment,
    ]);

    const table = useReactTable({
        columns,
        data: tableRows,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        enableRowSelection: true,
        enableMultiSort: !usePaginatedEnrollment,
        enableSortingRemoval: !usePaginatedEnrollment,
        manualSorting: usePaginatedEnrollment,
        getRowId: (row) => row.id.toString(),
        state: {
            rowSelection,
            sorting,
        },
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
    });

    const { rows } = table.getRowModel();
    const selectedRows = table.getFilteredSelectedRowModel();

    const fieldsToEnroll = useMemo(() => {
        return selectedRows.rows.map((row) => row.original);
    }, [selectedRows]);
    const fieldIdsToEnroll = useMemo(() => {
        return fieldsToEnroll.map((field) => field.id);
    }, [fieldsToEnroll]);
    const { downloadBoundaries, isDownloading } =
        useBulkBoundaryDownload(fieldIdsToEnroll);

    const selectedFieldsHaveActiveEnrollment = useMemo(() => {
        return fieldsToEnroll.some((field) => !!field.active_enrollment);
    }, [fieldsToEnroll]);

    const selectedCspOrganizationIds = useMemo(() => {
        return Array.from(
            new Set(
                fieldsToEnroll
                    .map((field) => field.grower.service_provider?.id)
                    .filter((id) => !!id),
            ),
        );
    }, [fieldsToEnroll]);

    const organizationCreditBalances = useGetRequest<
        OrganizationAvailableCredits[]
    >({
        url: "organizations/available_credits/",
        params: {
            organization_ids: selectedCspOrganizationIds.join(","),
        },
        enabled:
            userIsSentinel &&
            showEnrollmentForm &&
            selectedCspOrganizationIds.length > 0,
    });

    useEffect(() => {
        if (!usePaginatedEnrollment) return;
        invalidateAllRowsRequest();
    }, [
        effectiveOrdering,
        farmFilter,
        invalidateAllRowsRequest,
        searchValue,
        serviceProviderFilter,
        fieldStatus,
        usePaginatedEnrollment,
    ]);

    const fetchAllFilteredRows = useCallback(() => {
        return axiosRequest<FieldWithEnrollment[]>({
            url: "fields/enrollment_status_paginated/",
            params: {
                paginate: false,
                search: searchValue || undefined,
                service_provider: serviceProviderFilter,
                grower: farmFilter,
                status: fieldStatus,
                ordering: effectiveOrdering,
            },
        });
    }, [
        axiosRequest,
        effectiveOrdering,
        farmFilter,
        searchValue,
        serviceProviderFilter,
        fieldStatus,
    ]);

    const loadAllFilteredRows = useCallback(async () => {
        const requestId = ++allRowsRequestRef.current;
        setIsLoadingAllFilteredRows(true);

        try {
            const rows = await fetchAllFilteredRows();

            if (requestId !== allRowsRequestRef.current) {
                return null;
            }

            return rows;
        } catch (error) {
            if (requestId !== allRowsRequestRef.current) {
                return null;
            }

            throw error;
        } finally {
            if (requestId === allRowsRequestRef.current) {
                setIsLoadingAllFilteredRows(false);
            }
        }
    }, [fetchAllFilteredRows]);

    const openTotals = useCallback(async () => {
        if (!usePaginatedEnrollment) {
            onOpen();
            return;
        }

        const requestId = ++totalsRequestRef.current;
        try {
            const result = await refetchEnrollmentTotals();
            if (requestId !== totalsRequestRef.current) return;

            if (result.error || !result.data) {
                throw result.error;
            }
            onOpen();
        } catch {
            if (requestId !== totalsRequestRef.current) return;

            addToast({
                title: "Failed to load enrollment totals.",
                color: "danger",
            });
        }
    }, [onOpen, refetchEnrollmentTotals, usePaginatedEnrollment]);

    const downloadPDF = useCallback(async () => {
        let filteredData: FieldWithEnrollment[];

        if (usePaginatedEnrollment) {
            try {
                const rows = await loadAllFilteredRows();
                if (!rows) return;
                filteredData = rows;
            } catch {
                addToast({
                    title: "Failed to download enrollment PDF.",
                    color: "danger",
                });
                return;
            }
        } else {
            filteredData = legacyFilteredRows;
        }

        const columns = [
            "Status",
            "Farm",
            "Field",
            "Enrollment Status",
            "Enrollment",
            "Insights",
            "Acres",
            "Boundary Type",
            "Tier",
            "Price",
            "Total",
        ];
        const isSentinel = userIsSentinel;
        if (isSentinel) {
            columns.splice(1, 0, "CSP");
        }
        const rows = filteredData.map((field) => {
            const e = field.active_enrollment;
            const enrollmentDates = e
                ? `${formatDateMMDDYYYY(e.start_date)}\n${formatDateMMDDYYYY(e.end_date)}`
                : "";
            const insightsDates = e
                ? `${formatDateMMDDYYYY(e.insights_start_date)}\n${formatDateMMDDYYYY(e.insights_end_date)}`
                : "";
            const row = [
                field.status === Status.Active ? "Active" : "Inactive",
                field.grower.name,
                field.name,
                field.active_enrollment
                    ? field.active_enrollment.refunded_on
                        ? "Enrolled (Refunded)"
                        : field.active_enrollment.is_scheduled
                            ? "Scheduled"
                            : "Enrolled"
                    : "Inactive",
                enrollmentDates,
                insightsDates,
                field.active_enrollment?.acres ?? field.imagery_boundary_acres,
                field.imagery_extent == ImageryExtent.FIELD
                    ? "Field"
                    : "Irrigated",
                getEnrollmentTierLabel(
                    field.active_enrollment?.enrollment_tier,
                ),
                field.active_enrollment?.retail_price_per_acre,
                field.active_enrollment?.credits,
            ];
            if (isSentinel) {
                row.splice(1, 0, field.grower.service_provider?.name);
            }
            return row as (string | number)[];
        });

        const doc = new jsPDF();
        const image = new Image();
        image.src = sentinelLogo;
        image.onload = () => {
            doc.addImage(image, "PNG", 15, 10, 80, 20);
            const today = dayjs().format("MM/DD/YYYY");
            const pageWidth = doc.internal.pageSize.getWidth();
            doc.setFontSize(12);
            doc.text(`${today}`, pageWidth - 40, 20);
            const textWidth = doc.getTextWidth("Enrollment Summary");
            const x = (pageWidth - textWidth) / 2;
            doc.setFontSize(16);
            doc.text("Enrollment Summary", x, 35);
            doc.setFontSize(6);
            autoTable(doc, {
                columns: columns,
                body: rows,
                startY: 40,
                theme: "grid",
                bodyStyles: {
                    fontSize: 8,
                    overflow: "linebreak",
                },
                headStyles: {
                    fontSize: 8,
                    fillColor: [0, 51, 102],
                    textColor: [255, 255, 255],
                    overflow: "linebreak",
                },
            });
            doc.save("Sentinel Report.pdf");
        };
    }, [
        legacyFilteredRows,
        loadAllFilteredRows,
        userIsSentinel,
        formatDateMMDDYYYY,
        usePaginatedEnrollment,
    ]);

    const ref = useRef<HTMLDivElement>(null);

    const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
        count: rows.length,
        estimateSize: () => 33,
        getScrollElement: () => ref.current,
        measureElement:
            typeof window !== "undefined" &&
                navigator.userAgent.indexOf("Firefox") === -1
                ? (element) => element?.getBoundingClientRect().height
                : undefined,
        overscan: 5,
    });

    const virtualRows = rowVirtualizer.getVirtualItems();
    const {
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isFetchNextPageError,
    } = paginatedEnrollmentQuery;

    useEffect(() => {
        if (!usePaginatedEnrollment) return;

        const lastItem = virtualRows.at(-1);
        if (!lastItem) return;

        if (
            lastItem.index >= rows.length - 1 &&
            hasNextPage &&
            !isFetchingNextPage &&
            !isFetchNextPageError
        ) {
            fetchNextPage();
        }
    }, [
        fetchNextPage,
        hasNextPage,
        isFetchNextPageError,
        isFetchingNextPage,
        rows.length,
        usePaginatedEnrollment,
        virtualRows,
    ]);

    const summary = useMemo(() => {
        if (usePaginatedEnrollment) {
            const totalsByTier = new Map(
                (enrollmentTotals?.enrollments ?? []).map(
                    (total) => [total.enrollment_tier, total] as const,
                ),
            );
            const standard = totalsByTier.get(EnrollmentTier.Standard);
            const advanced = totalsByTier.get(EnrollmentTier.AdvancedV2);
            const complete = totalsByTier.get(EnrollmentTier.Complete);
            const irrigation = totalsByTier.get(EnrollmentTier.Irrigation);
            const monitoring = totalsByTier.get(EnrollmentTier.Monitoring);
            const preview = totalsByTier.get(EnrollmentTier.Preview);

            return {
                standardAcres: standard?.acres ?? 0,
                standardValue: standard?.value ?? 0,
                advancedAcres: advanced?.acres ?? 0,
                advancedValue: advanced?.value ?? 0,
                completeAcres: complete?.acres ?? 0,
                completeValue: complete?.value ?? 0,
                irrigationAcres: irrigation?.acres ?? 0,
                irrigationValue: irrigation?.value ?? 0,
                monitoringAcres: monitoring?.acres ?? 0,
                monitoringValue: monitoring?.value ?? 0,
                previewAcres: preview?.acres ?? 0,
                previewValue: preview?.value ?? 0,
                enrolledAcres:
                    (standard?.acres ?? 0) +
                    (advanced?.acres ?? 0) +
                    (complete?.acres ?? 0) +
                    (irrigation?.acres ?? 0),
                enrolledValue:
                    (standard?.value ?? 0) +
                    (advanced?.value ?? 0) +
                    (complete?.value ?? 0) +
                    (irrigation?.value ?? 0),
                activeUnenrolledAcres:
                    enrollmentTotals?.active_unenrolled_acres ?? 0,
                inactiveUnenrolledAcres:
                    enrollmentTotals?.inactive_unenrolled_acres ?? 0,
            };
        }

        return (
            legacyTotalsRows.reduce(
                (acc, fe) => {
                    if (fe.grower.service_provider?.id === SENTRY_PROVIDER_ID) {
                        return acc;
                    }
                    if (fe.active_enrollment) {
                        if (
                            fe.active_enrollment.enrollment_tier ==
                            EnrollmentTier.Standard
                        ) {
                            acc.standardAcres += fe.active_enrollment.acres;
                            acc.standardValue += fe.active_enrollment.credits;
                        } else if (
                            fe.active_enrollment.enrollment_tier ==
                            EnrollmentTier.AdvancedV2
                        ) {
                            acc.advancedAcres += fe.active_enrollment.acres;
                            acc.advancedValue += fe.active_enrollment.credits;
                        } else if (
                            fe.active_enrollment.enrollment_tier ==
                            EnrollmentTier.Complete
                        ) {
                            acc.completeAcres += fe.active_enrollment.acres;
                            acc.completeValue += fe.active_enrollment.credits;
                        } else if (
                            fe.active_enrollment.enrollment_tier ==
                            EnrollmentTier.Irrigation
                        ) {
                            acc.irrigationAcres += fe.active_enrollment.acres;
                            acc.irrigationValue += fe.active_enrollment.credits;
                        } else if (
                            fe.active_enrollment.enrollment_tier ==
                            EnrollmentTier.Monitoring
                        ) {
                            acc.monitoringAcres += fe.active_enrollment.acres;
                            acc.monitoringValue += fe.active_enrollment.credits;
                        } else if (
                            fe.active_enrollment.enrollment_tier ==
                            EnrollmentTier.Preview
                        ) {
                            acc.previewAcres += fe.active_enrollment.acres;
                            acc.previewValue += fe.active_enrollment.credits;
                        }
                        if (
                            [
                                EnrollmentTier.Standard,
                                EnrollmentTier.AdvancedV2,
                                EnrollmentTier.Complete,
                                EnrollmentTier.Irrigation,
                                EnrollmentTier.Monitoring,
                                EnrollmentTier.Preview,
                            ].includes(fe.active_enrollment.enrollment_tier)
                        ) {
                            acc.enrolledAcres += fe.active_enrollment.acres;
                            acc.enrolledValue += fe.active_enrollment.credits;
                        }
                    } else {
                        if (fe.status == Status.Active) {
                            acc.activeUnenrolledAcres +=
                                fe.imagery_boundary_acres;
                        } else {
                            acc.inactiveUnenrolledAcres +=
                                fe.imagery_boundary_acres;
                        }
                    }
                    return acc;
                },
                {
                    standardAcres: 0,
                    advancedAcres: 0,
                    completeAcres: 0,
                    irrigationAcres: 0,
                    monitoringAcres: 0,
                    previewAcres: 0,
                    enrolledAcres: 0,
                    standardValue: 0,
                    advancedValue: 0,
                    completeValue: 0,
                    irrigationValue: 0,
                    monitoringValue: 0,
                    previewValue: 0,
                    enrolledValue: 0,
                    activeUnenrolledAcres: 0,
                    inactiveUnenrolledAcres: 0,
                },
            ) ?? {}
        );
    }, [enrollmentTotals, legacyTotalsRows, usePaginatedEnrollment]);

    const requestHelp = useMutateRequest({
        url: `field_enrollments/request_help/`,
        method: "post",
        errorMessage: "Failed to request help",
        successMessage: "Your request has been sent to your account manager",
    });

    const requestAdditionalBalance = useCallback(
        () =>
            requestHelp.mutate({
                data: {
                    type: RequestHelpType.request_balance_update,
                },
            }),
        [requestHelp],
    );

    const isEnrollmentLoading = usePaginatedEnrollment
        ? paginatedEnrollmentQuery.isLoading
        : waitingForSentinelFeatureFlags || enrollmentQuery.isLoading;

    const initialEnrollmentError =
        usePaginatedEnrollment &&
        paginatedEnrollmentQuery.isError &&
        !paginatedEnrollmentQuery.data?.pages.length;

    const showEnrollmentEmptyState =
        usePaginatedEnrollment &&
        !isEnrollmentLoading &&
        !initialEnrollmentError &&
        tableRows.length === 0;

    const enrollmentActionsDisabled =
        usePaginatedEnrollment &&
        (isEnrollmentLoading || Boolean(initialEnrollmentError));

    return (
        <div className="p-2 flex flex-col w-full h-full">
            <div className="flex flex-row justify-between items-center">
                <div className="flex flex-row gap-3 items-center">
                    <FieldFilterBar
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        serviceProviderId={serviceProviderFilter}
                        onServiceProviderChange={setServiceProviderFilter}
                        serviceProviderOptions={serviceProviderOptions?.map(
                            (sp) => ({
                                key: sp.id.toString(),
                                label: sp.name,
                            }),
                        )}
                        growerId={farmFilter}
                        onGrowerChange={setFarmFilter}
                        growerOptions={farmOptions?.map((g) => ({
                            key: g.id.toString(),
                            label: g.name,
                        }))}
                    />
                    <Select
                        size="sm"
                        aria-label="Field Status"
                        selectedKeys={[fieldStatusFilter]}
                        onChange={(event) => {
                            setFieldStatusFilter(
                                event.target.value as FieldStatusFilter,
                            );
                        }}
                        className="w-40"
                    >
                        <SelectItem key="active">Active Fields</SelectItem>
                        <SelectItem key="inactive">Inactive Fields</SelectItem>
                        <SelectItem key="all">All Fields</SelectItem>
                    </Select>
                </div>
                <div className="flex flex-row gap-2 items-center">
                    {isConsultant && (
                        <div className="text-sm flex items-center gap-2 min-w-[500px]">
                            <span>Remaining Retail Balance:</span>
                            <Chip color="primary" radius="sm" variant="flat">
                                {formatCurrency(
                                    remainingBalance?.data
                                        ?.remaining_retail_value,
                                )}
                            </Chip>
                            <Button
                                size="sm"
                                color="primary"
                                onPress={requestAdditionalBalance}
                                isLoading={requestHelp.isPending}
                            >
                                Request Additional Balance
                            </Button>
                        </div>
                    )}
                    <Button
                        color="primary"
                        onPress={() => {
                            void openTotals();
                        }}
                        size="sm"
                        isLoading={
                            usePaginatedEnrollment && isFetchingEnrollmentTotals
                        }
                        isDisabled={enrollmentActionsDisabled}
                        startContent={<FontAwesomeIcon icon={faMoneyCheck} />}
                    >
                        Totals
                    </Button>
                    <Button
                        color="primary"
                        onPress={() => {
                            downloadPDF();
                        }}
                        size="sm"
                        isLoading={
                            usePaginatedEnrollment && isLoadingAllFilteredRows
                        }
                        isDisabled={enrollmentActionsDisabled}
                        startContent={<FontAwesomeIcon icon={faDownload} />}
                    >
                        Download PDF
                    </Button>
                    <Dropdown>
                        <DropdownTrigger>
                            <Button
                                color="secondary"
                                size="sm"
                                startContent={<FontAwesomeIcon icon={faBars} />}
                                isDisabled={selectedRows.rows.length === 0}
                            >
                                Actions
                            </Button>
                        </DropdownTrigger>
                        <DropdownMenu
                            disabledKeys={
                                selectedFieldsHaveActiveEnrollment
                                    ? ["enroll-selected-fields"]
                                    : []
                            }
                        >
                            <DropdownItem
                                key="enroll-selected-fields"
                                startContent={
                                    <FontAwesomeIcon
                                        className="text-primary-500"
                                        icon={faPlus}
                                    />
                                }
                                onPress={() => {
                                    setShowEnrollmentForm(true);
                                }}
                            >
                                Enroll
                            </DropdownItem>
                            <DropdownItem
                                key="activate-selected-fields"
                                startContent={
                                    <FontAwesomeIcon
                                        className="text-primary-500"
                                        icon={faToggleOn}
                                    />
                                }
                                onPress={() => {
                                    patchField.mutate(
                                        {
                                            data: selectedRows.rows.map(
                                                (row) => ({
                                                    id: row.original.id,
                                                    status: 1,
                                                }),
                                            ),
                                        },
                                        {
                                            onSuccess: () => {
                                                refetchEnrollmentFields();
                                            },
                                        },
                                    );
                                }}
                            >
                                Activate
                            </DropdownItem>
                            <DropdownItem
                                key="deactivate-selected-fields"
                                startContent={
                                    <FontAwesomeIcon
                                        className="text-primary-500"
                                        icon={faToggleOff}
                                    />
                                }
                                onPress={() => {
                                    patchField.mutate(
                                        {
                                            data: selectedRows.rows.map(
                                                (row) => ({
                                                    id: row.original.id,
                                                    status: 2,
                                                }),
                                            ),
                                        },
                                        {
                                            onSuccess: () => {
                                                refetchEnrollmentFields();
                                            },
                                        },
                                    );
                                }}
                            >
                                Deactivate
                            </DropdownItem>
                            <DropdownItem
                                key="download-shapefiles"
                                startContent={
                                    <FontAwesomeIcon
                                        className="text-primary-500"
                                        icon={faFileZipper}
                                    />
                                }
                                onPress={downloadBoundaries}
                                isDisabled={isDownloading}
                            >
                                {isDownloading
                                    ? "Downloading Shapefiles"
                                    : "Download Shapefiles"}
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </div>
            </div>
            <div
                ref={ref}
                className={classNames(
                    "overflow-y-auto text-sm bg-white w-full flex-1 min-h-0",
                    usePaginatedEnrollment && "relative",
                )}
            >
                {initialEnrollmentError ? (
                    <div className="h-full w-full flex flex-col items-center justify-center gap-3 text-gray-600">
                        <div>Failed to load enrollment fields.</div>
                        <Button
                            size="sm"
                            color="primary"
                            onPress={() => {
                                refetchEnrollmentFields();
                            }}
                        >
                            Retry
                        </Button>
                    </div>
                ) : showEnrollmentEmptyState ? (
                    <div className="h-full w-full flex items-center justify-center text-gray-500">
                        No enrollment fields found.
                    </div>
                ) : (
                    <table style={{ width: "100%" }}>
                        <thead
                            style={{
                                position: "sticky",
                                top: 0,
                                zIndex: 1,
                            }}
                            className="bg-white w-full"
                        >
                            {table.getHeaderGroups().map((group) => (
                                <tr className="border-b py-[0.25rem] border-gray-700 w-full flex flex-row items-center justify-items-center">
                                    {group.headers.map((header) => {
                                        return (
                                            <th
                                                aria-label={
                                                    header.column.columnDef.header?.toString() ??
                                                    "column header"
                                                }
                                                key={header.id}
                                                style={{
                                                    width: header.column.getSize(),
                                                }}
                                                className="pointer-events-none"
                                            >
                                                <div
                                                    onClick={header.column.getToggleSortingHandler()}
                                                    className={classNames({
                                                        "hover:bg-gray-200 rounded-lg p-1 text-sm pointer-events-auto w-full flex text-start":
                                                            true,
                                                        "cursor-pointer":
                                                            header.column.getCanSort(),
                                                    })}
                                                >
                                                    {flexRender(
                                                        header.column.columnDef
                                                            .header,
                                                        header.getContext(),
                                                    )}
                                                    {header.column.getCanSort() &&
                                                        header.column.getIsSorted() && (
                                                            <FontAwesomeIcon
                                                                className="ml-2 text-primary-500"
                                                                icon={
                                                                    header.column.getIsSorted() ===
                                                                    "asc"
                                                                        ? faArrowUpShortWide
                                                                        : faArrowDownShortWide
                                                                }
                                                                size="sm"
                                                            />
                                                        )}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody
                            style={{
                                height: `${rowVirtualizer.getTotalSize()}px`,
                                position: "relative",
                                width: table.getCenterTotalSize(),
                            }}
                        >
                            {virtualRows.map((virtualRow) => {
                                const row = rows[
                                    virtualRow.index
                                ] as Row<FieldWithEnrollment>;
                                return (
                                    <tr
                                        data-index={virtualRow.index}
                                        ref={(node) =>
                                            rowVirtualizer.measureElement(node)
                                        }
                                        key={row.id}
                                        style={{
                                            position: "absolute",
                                            transform: `translateY(${virtualRow.start}px)`,
                                            width: "100%",
                                        }}
                                        className="border-b py-[0.4rem] border-gray-700 flex flex-row items-center justify-items-center"
                                    >
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <td
                                                    key={cell.id}
                                                    style={{
                                                        width: cell.column.getSize(),
                                                    }}
                                                >
                                                    {flexRender(
                                                        cell.column.columnDef
                                                            .cell,
                                                        cell.getContext(),
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
                {usePaginatedEnrollment && isFetchNextPageError && (
                    <div className="h-24 w-full flex flex-col gap-2 items-center justify-center text-gray-600">
                        <div>Failed to load more fields.</div>
                        <Button
                            size="sm"
                            color="primary"
                            onPress={() => {
                                fetchNextPage();
                            }}
                        >
                            Retry
                        </Button>
                    </div>
                )}
                {usePaginatedEnrollment &&
                    paginatedEnrollmentQuery.isFetchingNextPage &&
                    !isFetchNextPageError && (
                        <div className="h-24 w-full flex items-center justify-center">
                            <Spinner color="primary" label="Loading..." />
                        </div>
                    )}
                {usePaginatedEnrollment &&
                    !paginatedEnrollmentQuery.hasNextPage &&
                    tableRows.length > 0 && (
                        <div className="h-16 w-full flex items-center justify-center text-gray-500">
                            End of data
                        </div>
                    )}
                {isEnrollmentLoading && (
                    <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
                        <Spinner size="lg" color="primary" label="Loading..." />
                    </div>
                )}
            </div>
            {showEnrollmentForm && (
                <EnrollmentForm
                    fieldsToEnroll={fieldsToEnroll}
                    remainingBalance={
                        remainingBalance?.data?.remaining_retail_value ?? 0
                    }
                    organizationCreditBalances={organizationCreditBalances.data}
                    organizationCreditBalancesLoading={
                        organizationCreditBalances.isFetching
                    }
                    organizationCreditBalancesError={
                        organizationCreditBalances.isError
                    }
                    existingEnrollment={
                        fieldsToEnroll[0].active_enrollment
                            ? fieldsToEnroll[0]
                            : undefined
                    }
                    onClose={(shouldRefetch) => {
                        if (shouldRefetch) {
                            refetchEnrollmentFields();
                        }
                        setShowEnrollmentForm(false);
                    }}
                />
            )}
            {showRefundForm && (
                <RefundForm
                    enrollment={fieldsToEnroll[0]}
                    onClose={() => {
                        setShowRefundForm(false);
                        refetchEnrollmentFields();
                    }}
                />
            )}
            {isOpen && (
                <Modal isOpen={isOpen} onClose={onClose}>
                    <ModalContent>
                        <ModalHeader>Totals</ModalHeader>
                        <ModalBody>
                            <Card>
                                <CardHeader className="text-lg font-semibold pb-0">
                                    Enrolled Fields
                                </CardHeader>
                                <Divider />
                                <CardBody>
                                    <Table removeWrapper>
                                        <TableHeader>
                                            <TableColumn
                                                key="tier"
                                                align="start"
                                                hideHeader={true}
                                            >
                                                Tier
                                            </TableColumn>
                                            <TableColumn
                                                key="acres"
                                                align="end"
                                            >
                                                Acres
                                            </TableColumn>
                                            <TableColumn
                                                key="value"
                                                align="end"
                                            >
                                                Value
                                            </TableColumn>
                                        </TableHeader>
                                        <TableBody>
                                            {(organizationPreviewEnabled ||
                                                !userIsSentinel) &&
                                                ((
                                                    <TableRow>
                                                        <TableCell>
                                                            Preview
                                                        </TableCell>
                                                        <TableCell>
                                                            {summary.previewAcres.toLocaleString()}
                                                        </TableCell>
                                                        <TableCell>
                                                            {formatCurrency(
                                                                summary.previewValue,
                                                            )}
                                                        </TableCell>
                                                    </TableRow> // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                ) as any)}
                                            <TableRow>
                                                <TableCell>Standard</TableCell>
                                                <TableCell>
                                                    {summary.standardAcres.toLocaleString()}
                                                </TableCell>
                                                <TableCell>
                                                    {formatCurrency(
                                                        summary.standardValue,
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    Irrigation
                                                </TableCell>
                                                <TableCell>
                                                    {summary.irrigationAcres.toLocaleString()}
                                                </TableCell>
                                                <TableCell>
                                                    {formatCurrency(
                                                        summary.irrigationValue,
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            {!!organizationMonitoringEnabled && (
                                                <TableRow>
                                                    <TableCell>
                                                        Monitoring
                                                    </TableCell>
                                                    <TableCell>
                                                        {summary.monitoringAcres.toLocaleString()}
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatCurrency(
                                                            summary.monitoringValue,
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            <TableRow>
                                                <TableCell>Advanced</TableCell>
                                                <TableCell>
                                                    {summary.advancedAcres.toLocaleString()}
                                                </TableCell>
                                                <TableCell>
                                                    {formatCurrency(
                                                        summary.advancedValue,
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Complete</TableCell>
                                                <TableCell>
                                                    {summary.completeAcres.toLocaleString()}
                                                </TableCell>
                                                <TableCell>
                                                    {formatCurrency(
                                                        summary.completeValue,
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow className="bg-gray-100">
                                                <TableCell className="font-bold">
                                                    Totals
                                                </TableCell>
                                                <TableCell className="font-bold">
                                                    {summary.enrolledAcres.toLocaleString()}
                                                </TableCell>
                                                <TableCell className="font-bold">
                                                    {formatCurrency(
                                                        summary.enrolledValue,
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </CardBody>
                            </Card>

                            <Card>
                                <CardHeader className="text-lg font-semibold pb-0">
                                    Unenrolled Fields
                                </CardHeader>
                                <Divider />
                                <CardBody>
                                    <Table removeWrapper>
                                        <TableHeader>
                                            <TableColumn
                                                key="active"
                                                align="start"
                                            >
                                                Active Field Acres
                                            </TableColumn>
                                            <TableColumn
                                                key="inactive"
                                                align="start"
                                            >
                                                Inactive Field Acres
                                            </TableColumn>
                                            <TableColumn
                                                key="total"
                                                align="end"
                                            >
                                                Totals
                                            </TableColumn>
                                        </TableHeader>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    {summary.activeUnenrolledAcres.toLocaleString()}
                                                </TableCell>
                                                <TableCell>
                                                    {summary.inactiveUnenrolledAcres.toLocaleString()}
                                                </TableCell>
                                                <TableCell>
                                                    {(
                                                        summary.activeUnenrolledAcres +
                                                        summary.inactiveUnenrolledAcres
                                                    ).toLocaleString()}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </CardBody>
                            </Card>
                        </ModalBody>
                    </ModalContent>
                </Modal>
            )}
        </div>
    );
}
